/**
 * FAB Grammar tokenizer + parser — per E-023 §B.
 *
 * Recursive-descent parser, hand-rolled. ~400 LOC, zero dependencies.
 * Forgiving by construction — every error path produces a partial AST and
 * appends to `errors[]` rather than throwing.
 *
 *   query       = expr ( WS expr )*
 *   expr        = predicate | phrase | negated | grouped | bareword
 *   predicate   = field ":" value
 *   field       = [a-z][a-z0-9_]*
 *   value       = quoted | bareword
 *   phrase      = '"' [^"]+ '"'
 *   negated     = "-" expr | "NOT" WS expr
 *   grouped     = "(" query ")"
 *   bareword    = [^\s"():\-][^\s]*
 *   joiner      = "AND" | "OR"     (* AND is implicit between adjacent expressions *)
 *
 * The lexer + parser are intentionally fused. The token stream isn't useful
 * standalone — operators are context-sensitive (a `:` is field/value
 * separator only inside a predicate), so we tokenize-on-demand.
 */
import { EMPTY_AND, } from "./ast.js";
const WS_RE = /\s/;
function peek(c, offset = 0) {
    return c.src[c.pos + offset] ?? "";
}
function consume(c) {
    const ch = c.src[c.pos] ?? "";
    c.pos++;
    return ch;
}
function skipWhitespace(c) {
    while (c.pos < c.src.length && WS_RE.test(c.src[c.pos]))
        c.pos++;
}
/**
 * Try to match a keyword (case-insensitive) at the current position followed
 * by either whitespace, end-of-input, or `(`. Returns true and consumes the
 * keyword on success; returns false and leaves the cursor untouched on miss.
 *
 * The followed-by check is what prevents `AND` inside a longer bareword
 * (e.g., `ANDREW`) from being mis-tokenized as the AND operator.
 */
function matchKeyword(c, kw) {
    if (c.pos + kw.length > c.src.length)
        return false;
    const slice = c.src.slice(c.pos, c.pos + kw.length);
    if (slice.toUpperCase() !== kw)
        return false;
    const after = c.src[c.pos + kw.length] ?? "";
    if (after !== "" && !WS_RE.test(after) && after !== "(" && after !== ")") {
        return false;
    }
    c.pos += kw.length;
    return true;
}
function isFieldStart(ch) {
    return ch >= "a" && ch <= "z";
}
function isFieldChar(ch) {
    return ((ch >= "a" && ch <= "z") || (ch >= "0" && ch <= "9") || ch === "_");
}
function isBarewordStart(ch) {
    // Anything except whitespace, quote, paren, colon, or leading dash.
    if (ch === "" || WS_RE.test(ch))
        return false;
    if (ch === '"' || ch === "(" || ch === ")" || ch === ":" || ch === "-") {
        return false;
    }
    return true;
}
function isBarewordChar(ch) {
    if (ch === "" || WS_RE.test(ch))
        return false;
    // Inside a bareword, parens still terminate (so `(x)` produces 3 tokens, not
    // 1) but colons and dashes are allowed mid-word (so `o-brien` and `1:1` parse
    // as single barewords). The grammar's first-char rule handles disambiguation.
    if (ch === '"' || ch === "(" || ch === ")")
        return false;
    return true;
}
/** Read a bareword token starting at the cursor. */
function readBareword(c) {
    let out = "";
    while (c.pos < c.src.length && isBarewordChar(c.src[c.pos])) {
        out += consume(c);
    }
    return out;
}
/** Read a quoted phrase. Cursor must be on the opening `"`. */
function readQuoted(c) {
    const start = c.pos;
    consume(c); // opening "
    let out = "";
    let closed = false;
    while (c.pos < c.src.length) {
        const ch = consume(c);
        if (ch === '"') {
            closed = true;
            break;
        }
        out += ch;
    }
    if (!closed) {
        c.errors.push({
            code: "UNCLOSED_QUOTE",
            message: `Unclosed quote starting at position ${start}`,
            pos: start,
        });
    }
    return { value: out, closed, start };
}
/** Read a [a-z][a-z0-9_]* field name. Returns empty string on miss. */
function readField(c) {
    let out = "";
    if (c.pos >= c.src.length || !isFieldStart(c.src[c.pos]))
        return out;
    while (c.pos < c.src.length && isFieldChar(c.src[c.pos])) {
        out += consume(c);
    }
    return out;
}
/**
 * Parse a single primary expression: predicate | phrase | negated | grouped |
 * bareword. Returns null if there's nothing parseable (e.g. just whitespace).
 */
function parsePrimary(c) {
    skipWhitespace(c);
    if (c.pos >= c.src.length)
        return null;
    const ch = peek(c);
    // ─── Negation: -x or NOT x ─────────────────────────────────────────────
    if (ch === "-") {
        const start = c.pos;
        consume(c);
        // Bare `-` at end of input or before whitespace = orphan dash.
        if (c.pos >= c.src.length || WS_RE.test(peek(c)) || peek(c) === ")") {
            c.errors.push({
                code: "ORPHAN_DASH",
                message: "Trailing `-` with no operand to negate",
                pos: start,
            });
            return null;
        }
        const child = parsePrimary(c);
        if (!child) {
            c.errors.push({
                code: "ORPHAN_DASH",
                message: "`-` followed by no parseable expression",
                pos: start,
            });
            return null;
        }
        const out = { kind: "not", child };
        return out;
    }
    // NOT keyword
    if (ch === "N" || ch === "n") {
        const saved = c.pos;
        if (matchKeyword(c, "NOT")) {
            skipWhitespace(c);
            const child = parsePrimary(c);
            if (!child) {
                c.errors.push({
                    code: "ORPHAN_DASH",
                    message: "`NOT` followed by no parseable expression",
                    pos: saved,
                });
                return null;
            }
            return { kind: "not", child };
        }
        // Restore (the keyword didn't match — it's a bareword starting with N)
        c.pos = saved;
    }
    // ─── Grouped: (query) ──────────────────────────────────────────────────
    if (ch === "(") {
        const start = c.pos;
        consume(c); // opening (
        const inner = parseQuery(c, /* untilClose */ true);
        if (peek(c) === ")") {
            consume(c);
        }
        else {
            c.errors.push({
                code: "UNCLOSED_PAREN",
                message: `Unclosed paren starting at position ${start}`,
                pos: start,
            });
        }
        return inner;
    }
    // ─── Stray closing paren ───────────────────────────────────────────────
    if (ch === ")") {
        c.errors.push({
            code: "EXTRA_PAREN",
            message: `Stray closing paren at position ${c.pos}`,
            pos: c.pos,
        });
        consume(c);
        return null;
    }
    // ─── Phrase: "..." ────────────────────────────────────────────────────
    if (ch === '"') {
        const phrase = readQuoted(c);
        return { kind: "phrase", value: phrase.value };
    }
    // ─── Predicate or bareword starting with a letter ──────────────────────
    // A predicate is `[a-z][a-z0-9_]*:value`. Try it first; on miss, treat the
    // characters as the start of a bareword.
    if (isFieldStart(ch)) {
        const saved = c.pos;
        const field = readField(c);
        if (field && peek(c) === ":") {
            consume(c); // colon
            // Empty predicate (e.g. `book:` with no value) is an error but recoverable.
            if (c.pos >= c.src.length ||
                WS_RE.test(peek(c)) ||
                peek(c) === ")") {
                c.errors.push({
                    code: "EMPTY_PREDICATE",
                    message: `Predicate \`${field}:\` has no value`,
                    pos: saved,
                });
                return null;
            }
            // Quoted value
            if (peek(c) === '"') {
                const phrase = readQuoted(c);
                return {
                    kind: "predicate",
                    field: field.toLowerCase(),
                    value: phrase.value,
                    quoted: true,
                };
            }
            // Bareword value
            const value = readBareword(c);
            return {
                kind: "predicate",
                field: field.toLowerCase(),
                value,
                quoted: false,
            };
        }
        // Not a predicate — restore and fall through to bareword path. The
        // bareword reader will pick up the same characters again.
        c.pos = saved;
    }
    // ─── Bareword (the catch-all) ──────────────────────────────────────────
    if (isBarewordStart(ch)) {
        // The first char is OK; readBareword will consume contiguous bareword chars.
        const word = readBareword(c);
        if (!word)
            return null;
        return { kind: "fuzzy", value: word };
    }
    // We can't make sense of this character — skip it and continue. This keeps
    // the parser from infinite-looping on unknown junk.
    consume(c);
    return null;
}
/**
 * Parse the next expression-with-leading-binary-operator.
 *
 * Returns null when the next token is `)` or end-of-input or a stray operator
 * we couldn't recover from.
 */
function parseExpr(c) {
    skipWhitespace(c);
    return parsePrimary(c);
}
/**
 * Parse a sequence of expressions separated by AND/OR (with implicit AND
 * between adjacent primaries).
 *
 * `untilClose` — when true, stop at the next `)` and let the caller consume it.
 *                When false, parse to end-of-input.
 *
 * Operator precedence: AND binds tighter than OR (standard Boolean algebra).
 * `a OR b AND c` = `a OR (b AND c)`.
 */
function parseQuery(c, untilClose) {
    // Build a flat list of OR-groups. Each OR-group is itself an AND-list.
    // We tokenize joiners on the fly.
    let andGroup = [];
    const orGroups = [andGroup];
    // Tracks the last joiner consumed without a following operand. Cleared
    // whenever the next operand parses; surfaced as TRAILING_OPERATOR if loop
    // exits while still set.
    let pendingJoiner = null;
    while (c.pos < c.src.length) {
        skipWhitespace(c);
        if (c.pos >= c.src.length)
            break;
        if (untilClose && peek(c) === ")")
            break;
        // Detect a joiner BEFORE parsing the next expression. This handles the
        // edge case where the previous expression failed to parse and we need to
        // resync at the next joiner without infinite-looping.
        if (matchKeyword(c, "AND")) {
            // Implicit/explicit AND — same effect; just continue collecting into
            // current andGroup. But: if andGroup is empty, this is a leading AND
            // (operator without a left operand) — record and continue.
            if (andGroup.length === 0) {
                c.errors.push({
                    code: "TRAILING_OPERATOR",
                    message: "`AND` with no left operand",
                    pos: c.pos - 3,
                });
            }
            pendingJoiner = { kind: "AND", pos: c.pos - 3 };
            continue;
        }
        if (matchKeyword(c, "OR")) {
            if (andGroup.length === 0) {
                c.errors.push({
                    code: "TRAILING_OPERATOR",
                    message: "`OR` with no left operand",
                    pos: c.pos - 2,
                });
                // Don't start a new OR group on a leading OR — keep the same one.
                continue;
            }
            andGroup = [];
            orGroups.push(andGroup);
            pendingJoiner = { kind: "OR", pos: c.pos - 2 };
            continue;
        }
        const node = parseExpr(c);
        if (node) {
            andGroup.push(node);
            pendingJoiner = null;
        }
        else {
            // We didn't parse anything but we also didn't advance — protect against
            // infinite loops by force-consuming a character.
            // (parsePrimary already advances on garbage; this is belt + suspenders.)
            // No-op: parsePrimary always either advances or returns null at EOF.
        }
    }
    // Trailing operator (AND/OR consumed but no operand followed).
    if (pendingJoiner) {
        c.errors.push({
            code: "TRAILING_OPERATOR",
            message: `Trailing \`${pendingJoiner.kind}\` with no right operand`,
            pos: pendingJoiner.pos,
        });
    }
    // Detect trailing-operator: if the last orGroup is empty, the input ended
    // on a joiner.
    if (orGroups.length > 1 && orGroups[orGroups.length - 1].length === 0) {
        orGroups.pop();
    }
    // Collapse: each AND-group → AND node (or single child); orGroups → OR node
    // (or single child).
    const andNodes = orGroups
        .filter((g) => g.length > 0)
        .map((g) => (g.length === 1 ? g[0] : { kind: "and", children: g }));
    if (andNodes.length === 0)
        return EMPTY_AND;
    if (andNodes.length === 1)
        return andNodes[0];
    return { kind: "or", children: andNodes };
}
/**
 * Parse a FAB query string into an AST + recoverable errors.
 *
 * The parser **never throws**. Malformed input produces a best-effort partial
 * AST plus entries in `errors[]`. The bar treats `errors.length === 0` as
 * "search confidently"; non-empty errors as "show a soft hint and search
 * with what we got."
 */
export function parse(input) {
    const cursor = { src: input, pos: 0, errors: [] };
    const ast = parseQuery(cursor, /* untilClose */ false);
    return { ast, raw: input, errors: cursor.errors };
}
//# sourceMappingURL=grammar.js.map